/**
 * Auth V3 — Business Device Pairing, device-side REST client.
 *
 * All three device endpoints (start / poll / complete) are PUBLIC and are called
 * with a plain axios instance (no Authorization header) — the device has no token
 * until it completes pairing. The admin `claim` endpoint lives in ./admin/claim-api.
 *
 * Success bodies are wrapped as { result: {...} }; errors come back as
 * HTTP 400 { errorCode: '...' }.
 *
 * See docs/authentication/business-device-pairing-v3-client.md (endpoints 1–3).
 */

import type {
  DevicePairPollResponse,
  DeviceMigrationRequest,
  DevicePairStartResponse,
  DeviceMigrationResponse,
  DevicePairCompleteRequest,
  DeviceRefreshStartRequest,
  DevicePairCompleteResponse,
  DeviceRefreshStartResponse,
  DeviceRefreshCompleteRequest,
  DeviceRefreshCompleteResponse,
  DeviceMigrationEligibilityResponse,
} from '@am3nd-org/pointcraft-types';

import axios, { AxiosError } from 'src/utils/http/tracked-axios';

import { useSystemStore } from 'src/store/SystemStore';
import {
  reportFirstPartyGatewayFailure,
  reportFirstPartyTransportFailure,
} from 'src/features/connectivity/first-party-transport-signal';

import { apiEndpoints } from '../../axios';
import type { DevicePairStartInput } from './demo-access';
import { withWindowsDeviceSerial } from './windows-device-metadata';

// ----------------------------------------------------------------------

/** Normalized error carrying the backend `errorCode` when available. */
export class BusinessDeviceV3Error extends Error {
  readonly errorCode?: string;

  readonly httpStatus?: number;

  readonly retryAfterMs?: number;

  readonly supportReference?: string;

  readonly isNetworkError: boolean;

  readonly isTimeout: boolean;

  constructor(
    message: string,
    details: {
      errorCode?: string;
      httpStatus?: number;
      retryAfterMs?: number;
      supportReference?: string;
      isNetworkError?: boolean;
      isTimeout?: boolean;
    } = {}
  ) {
    super(message);
    this.name = 'BusinessDeviceV3Error';
    this.errorCode = details.errorCode;
    this.httpStatus = details.httpStatus;
    this.retryAfterMs = details.retryAfterMs;
    this.supportReference = details.supportReference;
    this.isNetworkError = details.isNetworkError ?? false;
    this.isTimeout = details.isTimeout ?? false;
  }
}

const publicClient = () =>
  axios.create({ baseURL: useSystemStore.getState().baseUrl, timeout: 20000 });

/** Turn any thrown value into a BusinessDeviceV3Error with the backend errorCode if present. */
const parseRetryAfterMs = (value: unknown): number | undefined => {
  if (typeof value !== 'string' || value.trim() === '') return undefined;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;
  const date = Date.parse(value);
  return Number.isFinite(date) ? Math.max(0, date - Date.now()) : undefined;
};

function toV3Error(error: unknown, fallback: string): BusinessDeviceV3Error {
  // publicClient targets the first-party API origin, so connectivity-relevant
  // failures feed the shared coordinator confirmation signal.
  if (!reportFirstPartyTransportFailure(error)) reportFirstPartyGatewayFailure(error);
  const axiosError = error as AxiosError<{ errorCode?: string; supportReference?: string }>;
  const errorCode = axiosError?.response?.data?.errorCode;
  return new BusinessDeviceV3Error(errorCode ? `${fallback} (${errorCode})` : fallback, {
    errorCode,
    httpStatus: axiosError?.response?.status,
    retryAfterMs: parseRetryAfterMs(axiosError?.response?.headers?.['retry-after']),
    supportReference: axiosError?.response?.data?.supportReference,
    isNetworkError: Boolean(axiosError?.isAxiosError && !axiosError.response),
    isTimeout: axiosError?.code === 'ECONNABORTED' || axiosError?.code === 'ETIMEDOUT',
  });
}

// ----------------------------------------------------------------------
// Device-side (public)

export async function startPairing(body: DevicePairStartInput): Promise<DevicePairStartResponse> {
  try {
    const request = await withWindowsDeviceSerial(body, true);
    const res = await publicClient().post(apiEndpoints.businessDevicesV3.start, request);
    return res.data.result as DevicePairStartResponse;
  } catch (error) {
    throw toV3Error(error, 'Failed to start device pairing');
  }
}

export async function pollPairing(pollingId: string): Promise<DevicePairPollResponse> {
  try {
    const res = await publicClient().get(apiEndpoints.businessDevicesV3.poll(pollingId));
    return res.data.result as DevicePairPollResponse;
  } catch (error) {
    throw toV3Error(error, 'Failed to fetch pairing status');
  }
}

export async function completePairing(
  body: DevicePairCompleteRequest
): Promise<DevicePairCompleteResponse> {
  try {
    const res = await publicClient().post(apiEndpoints.businessDevicesV3.complete, body);
    return res.data.result as DevicePairCompleteResponse;
  } catch (error) {
    throw toV3Error(error, 'Failed to complete device pairing');
  }
}

export async function startDeviceRefresh(
  body: DeviceRefreshStartRequest
): Promise<DeviceRefreshStartResponse> {
  try {
    const request = await withWindowsDeviceSerial(body);
    const res = await publicClient().post(apiEndpoints.businessDevicesV3.refreshStart, request);
    return res.data.result as DeviceRefreshStartResponse;
  } catch (error) {
    throw toV3Error(error, 'Failed to refresh device session');
  }
}

export async function completeDeviceRefresh(
  body: DeviceRefreshCompleteRequest
): Promise<DeviceRefreshCompleteResponse> {
  try {
    const res = await publicClient().post(apiEndpoints.businessDevicesV3.refreshComplete, body);
    return res.data.result as DeviceRefreshCompleteResponse;
  } catch (error) {
    throw toV3Error(error, 'Failed to complete device session refresh');
  }
}

// ----------------------------------------------------------------------
// Authenticated legacy-session migration

export async function migrateDevice(
  body: DeviceMigrationRequest,
  authToken: string
): Promise<DeviceMigrationResponse> {
  try {
    const request = await withWindowsDeviceSerial(body, true);
    const res = await publicClient().post(apiEndpoints.businessDevicesV3.migration, request, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    return res.data.result as DeviceMigrationResponse;
  } catch (error) {
    throw toV3Error(error, 'Failed to migrate device auth');
  }
}

export async function getMigrationEligibility(
  authToken: string
): Promise<DeviceMigrationEligibilityResponse> {
  try {
    const res = await publicClient().get('/api/auth/v3/devices/migration/eligibility', {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    return res.data.result as DeviceMigrationEligibilityResponse;
  } catch (error) {
    throw toV3Error(error, 'Failed to check device migration eligibility');
  }
}
